class BazaarVoice extends HTMLElement {
  constructor() {
    super();

    // Base styles applied to the Bazaarvoice shadow roots on EVERY page that
    // uses the widget (beverage + merch PDPs). BV renders the review display
    // widget and the in-page submission form inside shadow roots the theme's
    // regular stylesheet can't reach, so we adopt constructable stylesheets.
    const baseStyles = `
      :host {
        display: block;
      }
      .bv-ips-modal-window {
        height: 90% !important;
      }

      .dkdwd {
        width: 100% !important;
        max-width:500px;
      }

      .diNKxB, .ips__sc-emgfje-0, .cUmFrR {
        background-color: #cba14f !important;
        color: white !important;
        border: 0 !important;
      }

      .izbxwW {
        display: none !important;
      }


      .coRjPr, .cdREOE, #bv-reviews-rating-snapshot-container
       {
        padding-left: 0 !important;
        margin-left: 0 !important;
      }

      /* Style product links in reviews to be more discernible */
      a[href*="/products/"],
      .bv-content-product-link,
      .bv-content-reference-data a,
      [class*="ProductLink"] a,
      [class*="product-link"] a,
      [class*="productLink"] a {
        color: #8a6d35 !important;
        text-decoration: underline !important;
        text-underline-offset: 2px;
      }

      a[href*="/products/"]:hover,
      .bv-content-product-link:hover,
      .bv-content-reference-data a:hover,
      [class*="ProductLink"] a:hover,
      [class*="product-link"] a:hover,
      [class*="productLink"] a:hover {
        color: #6b5629 !important;
      }
    `;

    // Beverage-PDP-only customisations. These are scoped to beverage PDPs (see
    // the .beverage-product gate below) so the merch PDPs keep BV's default
    // submission flow. The submission form's shadow root is document-level and
    // identical regardless of which page opened it, so it cannot be scoped with
    // a CSS ancestor selector — we gate at injection time by page type instead.
    const bevStyles = `
      /* Shrink the submission modal to its content instead of always filling
         90% of the viewport. With the optional steps hidden (and especially in
         the post-submission "Completed" state) the fixed height left a large
         empty area below the content. The modal is its own scroll container, so
         max-height keeps a tall first-time form in-viewport without a 2nd bar. */
      .bv-ips-modal-window {
        height: auto !important;
        max-height: 90% !important;
        overflow-y: auto !important;
      }

      /* Hide the optional steps in the in-page review submission form, leaving
         only the required "Your reviews" step. BV's step <fieldset>s carry
         stable, semantic ids (#bv-ips-step-N) and each contains a header with a
         stable semantic id, but the step *number* is config-driven. Targeting
         the fieldset via :has() on the section's header id keeps this working
         even if BV reorders the steps. The ips__sc-* class names are hashed and
         change on every BV redeploy, so they are NOT safe to rely on. */
      fieldset[id^="bv-ips-step-"]:has(#bv-label-text-photoVideo-header),
      fieldset[id^="bv-ips-step-"]:has(#bv-label-text-personalInformation-header),
      fieldset[id^="bv-ips-step-"]:has(#bv-label-text-productrating-header) {
        display: none !important;
      }

      /* Submission modal header: enlarge the product thumbnail (it was tiny)
         and give it breathing room. It's the only <img> in the modal. */
      .bv-ips-modal-window img {
        width: 100px !important;
        height: 100px !important;
        margin: 24px 16px 24px 24px !important;
      }
      @media (max-width: 768px) {
        .bv-ips-modal-window img {
          width: 64px !important;
          height: 64px !important;
        }
      }

      /* Relabel the modal title "My Review" -> "Write a review for" (the second
         line is the product name, left intact) and tighten the gap between the
         two lines. The h1 carries a stable id (...bv-ips-title); swap the first
         span's text via ::before so it survives BV's React re-renders (a JS
         textContent swap would be undone). */
      .bv-ips-modal-window h1[id*="bv-ips-title"] {
        line-height: 1.1 !important;
      }
      .bv-ips-modal-window h1[id*="bv-ips-title"] > span {
        margin: 0 !important;
        line-height: 1.1 !important;
      }
      .bv-ips-modal-window h1[id*="bv-ips-title"] > span:first-of-type {
        font-size: 0 !important;
      }
      .bv-ips-modal-window h1[id*="bv-ips-title"] > span:first-of-type::before {
        content: "Write a review for";
        font-size: 23px;
        font-weight: 700;
        line-height: 1.1;
      }

      /* The "Cancel" button (shown next to Submit in the already-submitted /
         edit state) renders as near-invisible light grey on white. BV gives it
         a stable id; darken it so it's legible. */
      #bv-ips-cancel,
      #bv-ips-cancel span {
        color: #151515 !important;
        font-weight: 700 !important;
      }
    `;

    // Build the stylesheets once and reuse the same instances across every
    // shadow root so re-injection is idempotent.
    const baseSheet = new CSSStyleSheet();
    baseSheet.replaceSync(baseStyles);

    // Only layer the beverage-PDP customisations on beverage PDPs. The bev
    // product section adds the `.beverage-product` class; merch PDPs do not
    // have it (they carry `.main-product` only).
    const isBeveragePdp = !!document.querySelector('.beverage-product');
    const sheets = isBeveragePdp ? [baseSheet, new CSSStyleSheet()] : [baseSheet];
    if (isBeveragePdp) {
      // bevSheet is layered AFTER baseSheet so its modal-height override wins.
      sheets[1].replaceSync(bevStyles);
    }

    const inject = (shadowRoot) => {
      if (!shadowRoot) return;
      for (const sheet of sheets) {
        if (!shadowRoot.adoptedStyleSheets.includes(sheet)) {
          shadowRoot.adoptedStyleSheets = [...shadowRoot.adoptedStyleSheets, sheet];
        }
      }
    };

    // The review *display* widget renders in a shadow root nested under this
    // element. The *submission* form renders in a separate, document-level
    // <div data-bv-show="inpage_submission"> shadow root that only exists once
    // the user opens the form (from either the rating stars or "Write a
    // Review"). Cover both.
    let displayShadowRoot = null;
    const tick = () => {
      if (!displayShadowRoot) {
        const host = Array.from(this.querySelectorAll('*')).find(
          (el) => el.shadowRoot
        );
        if (host) {
          displayShadowRoot = host.shadowRoot;
        }
      }
      inject(displayShadowRoot);

      const submission = document.querySelector(
        'div[data-bv-show="inpage_submission"]'
      );
      if (submission) {
        inject(submission.shadowRoot);
      }
    };

    // Observe for immediacy (minimises any flash of the optional steps) and
    // poll as a fallback for BV's async/late loads.
    const observer = new MutationObserver(tick);
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });

    tick();
    const pollInterval = setInterval(tick, 500);

    // Stop the fallback poll after 30s; the MutationObserver keeps handling any
    // later re-opens of the submission form.
    setTimeout(() => clearInterval(pollInterval), 30000);
  }
}

customElements.define('bazaar-voice', BazaarVoice);
